/**
 * Diet Hub — clinical nutrition module (ported from JMDiabetes Diet Hub).
 * Contains 5 diet protocols (English + Marathi) with mechanism-of-action
 * science, 4 activity multipliers, and Mifflin-St Jeor calorie calculators.
 * Used by both DietHub.jsx and PrintDietPlan.jsx.
 */

export const DIET_TYPES = [
  {
    id: "dikshit",
    name_en: "Dikshit Diet",
    name_mr: "दीक्षित डाएट",
    desc_en: "Two-meal strategy for weight loss & insulin sensitivity.",
    desc_mr: "वजन कमी करण्यासाठी आणि इन्सुलिन संवेदनशीलतेसाठी दोन-वेळचे जेवण धोरण.",
    science_en:
      'Focuses on "Insulin Secretion Frequency". Every time we eat, the pancreas releases insulin. By eating only twice, we limit insulin spikes to just twice a day. This helps lower overall insulin resistance and forces the body to use stored fat for energy between meals.',
    science_mr:
      '"इन्सुलिन स्राव वारंवारता" वर लक्ष केंद्रित करते. प्रत्येक वेळी आपण खातो तेव्हा स्वादुपिंड इन्सुलिन सोडते. दिवसातून फक्त दोनदा जेवल्याने, आपण इन्सुलिनची वाढ दिवसातून फक्त दोन वेळा मर्यादित करतो.',
  },
  {
    id: "if",
    name_en: "Intermittent Fasting (16:8)",
    name_mr: "इंटरमिटंट फास्टिंग (१६:८)",
    desc_en: "Standard 16:8 protocol for metabolic rest.",
    desc_mr: "मेटाबॉलिक आरामासाठी मानक १६:८ प्रोटोकॉल.",
    science_en:
      'Works via "Metabolic Switching". During the 16-hour fast, your liver runs out of sugar (glycogen) and starts burning fat (ketosis). This "metabolic rest" lowers baseline insulin levels and triggers "Autophagy" — a process where cells clean out damaged components.',
    science_mr:
      '"मेटाबॉलिक स्विचिंग" द्वारे कार्य करते. १६ तासांच्या उपवासादरम्यान, तुमच्या यकृतातील साखर (ग्लायकोजेन) संपते आणि चरबी जळण्यास सुरुवात होते.',
  },
  {
    id: "dash",
    name_en: "DASH Diet",
    name_mr: "डॅश डाएट",
    desc_en: "Clinical approach to manage BP & sugar.",
    desc_mr: "बीपी आणि साखर नियंत्रित करण्यासाठी क्लिनिकल दृष्टीकोन.",
    science_en:
      'Focuses on "Vascular Health". Diabetics are at high risk for Heart Disease. DASH (Dietary Approaches to Stop Hypertension) uses high fiber and specific minerals like Potassium and Magnesium to relax blood vessels, reducing the strain on your heart and kidneys.',
    science_mr:
      '"रक्तवाहिन्यांच्या आरोग्यावर" लक्ष केंद्रित करते. मधुमेहींना हृदयरोगाचा मोठा धोका असतो. डॅश आहार उच्च फायबर आणि पोटॅशियम, मॅग्नेशियम यांसारख्या खनिजांचा वापर करून रक्तवाहिन्यांना आराम देतो.',
  },
  {
    id: "low-carb",
    name_en: "Low Carb High Protein",
    name_mr: "लो कार्ब हाय प्रोटीन",
    desc_en: "Minimises insulin spikes by limiting refined grains.",
    desc_mr: "प्रक्रिया केलेले धान्य मर्यादित करून इन्सुलिन स्पाइक्स कमी करते.",
    science_en:
      'Focuses on "Glycemic Load Management". By reducing grains (carbs), you prevent the sharp "sugar mountain" after meals. High protein increases satiety (fullness) and has a higher "Thermic Effect," meaning your body burns more calories just to digest the food.',
    science_mr:
      '"ग्लायसेमिक लोड व्यवस्थापन" वर लक्ष केंद्रित करते. धान्य (कार्ब्स) कमी करून, तुम्ही जेवणानंतर साखरेची होणारी वाढ रोखता.',
  },
  {
    id: "wfpb",
    name_en: "WFPB (Whole Food Plant Based)",
    name_mr: "WFPB (संपूर्ण वनस्पती आधारित आहार)",
    desc_en: "Reversing insulin resistance with fibre-rich plants.",
    desc_mr: "फायबरयुक्त वनस्पतींच्या सहाय्याने इन्सुलिन रेझिस्टन्स रिव्हर्स करणे.",
    science_en:
      'Focuses on "Fiber-Insulin Synergy". Unprocessed plants are packed with soluble fiber. This fiber forms a gel in your gut, trapping sugar and releasing it slowly. It also feeds good gut bacteria which produce chemicals that make your cells more sensitive to insulin.',
    science_mr:
      '"फायबर-इन्सुलिन सिनर्जी" वर लक्ष केंद्रित करते. प्रक्रिया न केलेल्या वनस्पती विद्राव्य फायबरने भरलेल्या असतात.',
  },
];

export const ACTIVITY_LEVELS = [
  { id: "sedentary", name_en: "Sedentary", name_mr: "कमी हालचाल", multiplier: 1.2, desc_en: "Little to no exercise" },
  { id: "light",     name_en: "Lightly Active", name_mr: "हलकी हालचाल", multiplier: 1.375, desc_en: "Light exercise 1-3 days/week" },
  { id: "moderate",  name_en: "Moderately Active", name_mr: "मध्यम हालचाल", multiplier: 1.55, desc_en: "Moderate exercise 3-5 days/week" },
  { id: "active",    name_en: "Very Active", name_mr: "खूप सक्रिय", multiplier: 1.725, desc_en: "Hard exercise 6-7 days/week" },
];

/** Mifflin-St Jeor BMR + activity TDEE + BMI-aware deficit target. */
export function computeMetabolicTargets({ age, weight_kg, height_cm, sex, activityId }) {
  const w = parseFloat(weight_kg);
  const h = parseFloat(height_cm);
  const a = parseFloat(age);
  if (!Number.isFinite(w) || !Number.isFinite(h) || !Number.isFinite(a)) return null;
  const bmi = +(w / ((h / 100) ** 2)).toFixed(1);
  let bmr = 10 * w + 6.25 * h - 5 * a;
  bmr += /male/i.test(sex || "") ? 5 : -161;
  const act = ACTIVITY_LEVELS.find((x) => x.id === activityId) || ACTIVITY_LEVELS[0];
  const tdee = Math.round(bmr * act.multiplier);
  // Asian BMI cut-off — 23 is overweight for South-Asian phenotypes.
  const targetCalories = bmi > 23 ? tdee - 400 : tdee;
  return { bmi, bmr: Math.round(bmr), tdee, targetCalories, multiplier: act.multiplier };
}
